import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Box,
  CircularProgress,
  Alert,
  AlertTitle,
  ThemeProvider,
  createTheme,
  CssBaseline
} from '@mui/material';
import WelcomeCard from './WelcomeCard';
import MetadataForm from './MetadataForm';
import InterviewChat from './InterviewChat';
import ThankYouCard from './ThankYouCard';
import researchService from '../../services/researchService';

// Custom MUI theme for interview pages
const interviewTheme = createTheme({
  palette: {
    primary: {
      main: '#667eea',
      light: '#8b9eff',
      dark: '#4a5fc1'
    },
    secondary: {
      main: '#764ba2',
      light: '#a676d4',
      dark: '#522f73'
    },
    background: {
      default: '#f5f7fa'
    }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
  },
  shape: {
    borderRadius: 12
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16
        }
      }
    }
  }
});

// Interview stages
const STAGES = {
  LOADING: 'loading',
  INVALID: 'invalid',
  WELCOME: 'welcome',
  METADATA: 'metadata',
  INTERVIEWING: 'interviewing',
  COMPLETED: 'completed'
};

/**
 * Main interview page component.
 * Handles the full interview flow from validation to completion.
 */
const InterviewPage = () => {
  const { surveyId } = useParams();

  const [stage, setStage] = useState(STAGES.LOADING);
  const [error, setError] = useState(null);
  const [surveyInfo, setSurveyInfo] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [initialMessage, setInitialMessage] = useState('');
  const [userMetadata, setUserMetadata] = useState(null);

  // Validate survey on mount
  useEffect(() => {
    validateSurvey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  const validateSurvey = async () => {
    try {
      setStage(STAGES.LOADING);
      setError(null);

      const response = await researchService.validateSurvey(surveyId);
      setSurveyInfo(response);
      setStage(STAGES.WELCOME);
    } catch (err) {
      console.error('Survey validation error:', err);
      setError(
        err.response?.data?.error ||
        'This survey is not available. Please check the URL and try again.'
      );
      setStage(STAGES.INVALID);
    }
  };

  const handleStartClick = () => {
    setStage(STAGES.METADATA);
  };

  const handleMetadataSubmit = async (metadata) => {
    try {
      setUserMetadata(metadata);
      setStage(STAGES.LOADING);

      const response = await researchService.startInterview(surveyId, metadata);

      setSessionId(response.session_id);
      setInitialMessage(response.initial_message || response.welcome_message);

      // Update survey info with any additional data from start response
      if (response.title) {
        setSurveyInfo(prev => ({ ...prev, title: response.title }));
      }

      setStage(STAGES.INTERVIEWING);
    } catch (err) {
      console.error('Error starting interview:', err);
      setError('Failed to start the interview. Please try again.');
      setStage(STAGES.INVALID);
    }
  };

  const handleInterviewComplete = () => {
    setStage(STAGES.COMPLETED);
  };

  const renderContent = () => {
    switch (stage) {
      case STAGES.LOADING:
        return (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            minHeight="60vh"
            gap={2}
          >
            <CircularProgress size={48} thickness={4} />
            <Box textAlign="center" color="text.secondary">
              {sessionId ? 'Starting interview...' : 'Loading survey...'}
            </Box>
          </Box>
        );

      case STAGES.INVALID:
        return (
          <Box maxWidth={500} mx="auto" mt={8}>
            <Alert severity="error" variant="filled">
              <AlertTitle>Survey Not Available</AlertTitle>
              {error || 'This survey could not be found or is no longer active.'}
            </Alert>
          </Box>
        );

      case STAGES.WELCOME:
        return (
          <WelcomeCard
            surveyInfo={surveyInfo}
            onStart={handleStartClick}
          />
        );

      case STAGES.METADATA:
        return (
          <MetadataForm
            onSubmit={handleMetadataSubmit}
            onBack={() => setStage(STAGES.WELCOME)}
          />
        );

      case STAGES.INTERVIEWING:
        return (
          <InterviewChat
            sessionId={sessionId}
            surveyInfo={surveyInfo}
            initialMessage={initialMessage}
            onComplete={handleInterviewComplete}
          />
        );

      case STAGES.COMPLETED:
        return (
          <ThankYouCard
            surveyInfo={surveyInfo}
            completionMessage={surveyInfo?.completion_message}
          />
        );

      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={interviewTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          py: 3
        }}
      >
        <Container maxWidth="lg">
          {renderContent()}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default InterviewPage;
